import { CrudMode, DataDisplay, Grid, ValidationErrors, pn, useElementBreakpoints } from "@compass/core";
import { LkRole, LkRoleId, TenantInfo, User, UserDto, UserEditResource, UserViewResource } from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { BasicInfo } from "./BasicInfo";
import { DataAssignment } from "./DataAssignment";
import { MatchAssignment } from "./MatchAssignment";
import { Texting } from "./Texting";
import { Permissions } from "./Permissions";
import { UserRole } from "./UserRole";
import { SystemInfo } from "./SystemInfo";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";

export const UserPageBody = (props: {
  crudMode: CrudMode;
  userDto: UserDto;
  tenantInfo: TenantInfo | undefined;
  appUserRoleId: number | undefined;
  userPermissions: number[];
  userMembership: string;
  setUserDto: (value: UserDto) => void;
  setUserPermissions: (value: number[]) => void;
  reroute: (userMembership: string, crudMode: CrudMode) => void;
  save: () => void;
  unsavedChanges: boolean;
  viewResource: UserViewResource;
  editResource: UserEditResource;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, setBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();

  // Gets the current logged in user's role
  const appUserRoleId: number | undefined = useSelector((state: RootState) => state.app.roleId);

  const [selectedRole, setSelectedRole] = useState<LkRole>();
  const generationValidationErrors = props.validationErrors[pn<User>("generalValidationErrors")];

  useEffect(() => {
    const role: LkRole | undefined = props.viewResource.roles?.find((x) => x.id === props.userDto.user?.roleId);
    setSelectedRole(role);
  }, [props.userDto.user?.roleId, props.viewResource]);

  return (
    <Grid ref={breakpointsRef} container spacing="lg">
      <Grid item xs={setBreakpoints({ md: 4, xs: 12 })}>
        <BasicInfo
          appUserRoleId={appUserRoleId}
          crudMode={props.crudMode}
          userDto={props.userDto}
          unsavedChanges={props.unsavedChanges}
          userMembership={props.userMembership}
          setUserDto={props.setUserDto}
          reroute={props.reroute}
          save={props.save}
          viewResource={props.viewResource}
          editResource={props.editResource}
          validationErrors={props.validationErrors}
        />
      </Grid>


      {(props.crudMode === "view" ||
        props.appUserRoleId === LkRoleId.Cobro ||
        props.appUserRoleId === LkRoleId.Administrator) && (
          <Grid item xs={setBreakpoints({ md: 4, xs: 12 })}>
            <UserRole
              appUserRoleId={appUserRoleId}
              crudMode={props.crudMode}
              userDto={props.userDto}
              setUserDto={props.setUserDto}
              userMembership={props.userMembership}
              viewResource={props.viewResource}
              editResource={props.editResource}
              validationErrors={props.validationErrors}
            />
          </Grid>
        )}


      {(props.crudMode === "view" ||
        props.appUserRoleId === LkRoleId.Cobro ||
        props.appUserRoleId === LkRoleId.Administrator) && (
          <Grid item xs={setBreakpoints({ md: 4, xs: 12 })}>
            <Permissions
              crudMode={props.crudMode}
              appUserRoleId={appUserRoleId}
              userDto={props.userDto}
              userMembership={props.userMembership}
              userPermissions={props.userPermissions || []}
              setUserDto={props.setUserDto}
              onUserPermissionsChange={props.setUserPermissions}
              viewResource={props.viewResource}
              editResource={props.editResource}
              validationErrors={props.validationErrors}
              selectedRole={selectedRole}
            />
          </Grid>
        )}

      {(props.crudMode === "view" ||
        props.appUserRoleId === LkRoleId.Cobro ||
        props.appUserRoleId === LkRoleId.Administrator) &&
        selectedRole?.canUseDataAssignment && (
          <Grid item xs={setBreakpoints({ md: 4, xs: 12 })}>
            <DataAssignment
              crudMode={props.crudMode}
              appUserRoleId={appUserRoleId}
              userDto={props.userDto}
              setUserDto={props.setUserDto}
              editResource={props.editResource}
              validationErrors={props.validationErrors}
              selectedRole={selectedRole}
            />
          </Grid>
        )}

      {(props.crudMode === "view" ||
        props.appUserRoleId === LkRoleId.Cobro ||
        props.appUserRoleId === LkRoleId.Administrator) &&
        selectedRole?.canUsePartnerAssignment && (
          <Grid item xs={setBreakpoints({ md: 4, xs: 12 })}>
            <MatchAssignment
              crudMode={props.crudMode}
              appUserRoleId={appUserRoleId}
              userDto={props.userDto}
              setUserDto={props.setUserDto}
              editResource={props.editResource}
              validationErrors={props.validationErrors}
              selectedRole={selectedRole}
            />
          </Grid>
        )}

      {(props.crudMode === "view" ||
        props.appUserRoleId === LkRoleId.Cobro ||
        props.appUserRoleId === LkRoleId.Administrator) &&
        props.tenantInfo?.canInfobip && (
          <Grid item xs={setBreakpoints({ md: 4, xs: 12 })}>
            <Texting
              crudMode={props.crudMode}
              userDto={props.userDto}
              setUserDto={props.setUserDto}
              userMembership={props.userMembership}
              viewResource={props.viewResource}
              editResource={props.editResource}
              validationErrors={props.validationErrors}
              selectedRole={selectedRole}
            />
          </Grid>
        )}

      {props.crudMode === "view" && (
        <Grid item xs={fullFieldBreakpoints}>
          <SystemInfo userDto={props.userDto} setUserDto={props.setUserDto} viewResource={props.viewResource} />
        </Grid>
      )}
    </Grid>
  );
};
